import {
  faCreditCard,
  faExclamationTriangle
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";

import { T } from "@repo/transifex";
import {
  PayButton,
  SecondaryButton
} from "~/components/kiosk/bottom-actions/bottom-actions";
import { SwishIcon } from "~/components/SwishIcon";
import { useKioskStore } from "~/routes/__app.kiosk/route";
import { useKioskProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products";
import { useKioskSessionLocale } from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { priceFormatter } from "~/utils/price-formatter";

import {
  getKioskPaymentButtons,
  type KioskPaymentMethod
} from "./get-kiosk-payment-buttons";
import { useKioskCheckoutStart } from "./use-kiosk-checkout-start";

type MethodButtonProps = {
  onClick: () => void;
  disabled?: boolean;
};

const CardPayButton = ({ onClick, disabled }: MethodButtonProps) => (
  <button
    type="button"
    className="flex flex-1 items-center justify-center gap-x-5 rounded-3xl bg-kiosk-actions-sheet-primary-background px-10 py-9 text-kiosk-actions-sheet-primary-text"
    onClick={onClick}
    disabled={disabled}
  >
    <FontAwesomeIcon icon={faCreditCard} fontSize={48} />
    <span className="text-4xl font-extrabold">
      <T _str="Pay with card" />
    </span>
  </button>
);

const SwishPayButton = ({ onClick, disabled }: MethodButtonProps) => (
  <button
    type="button"
    className="flex flex-1 items-center justify-center gap-x-5 rounded-3xl bg-white px-10 py-9 text-black"
    onClick={onClick}
    disabled={disabled}
  >
    <SwishIcon size={48} />
    <span className="text-4xl font-extrabold">
      <T _str="Pay with {swish}" swish="Swish" />
    </span>
  </button>
);

type KioskPaymentButtonsProps = {
  /** Runs before a payment starts; return false to block it (e.g. failed form validation). */
  onBeforeSubmit?: () => boolean | Promise<boolean>;
  disabled?: boolean;
};

/**
 * Renders the payment button(s) available for the current till and basket
 * total, and starts the corresponding payment flow on click.
 *
 * Mirrors the POS basket footer: a single available method (credit, or a till
 * with only card or only swish) collapses into one "Order & pay" button. We
 * only surface the individual method names when the user actually has a choice
 * between card and swish.
 */
export const KioskPaymentButtons = ({
  onBeforeSubmit,
  disabled
}: KioskPaymentButtonsProps) => {
  const till = useKioskStore((state) => state.till);
  const isUserIdentified = useKioskStore((state) => state.user !== undefined);
  const hidePrices = useKioskStore(
    (state) => state.till.moduleConfig.hidePrices
  );
  const locale = useKioskSessionLocale();

  const { unitTotalPayableInclVat, currency } = useKioskProducts();
  const startPayment = useKioskCheckoutStart();

  const { mutate: submitPayment, isPending } = useMutation({
    mutationFn: async (paymentMethod: KioskPaymentMethod) => {
      if (onBeforeSubmit && !(await onBeforeSubmit())) {
        return;
      }
      startPayment(paymentMethod);
    }
  });

  const buttons = getKioskPaymentButtons({
    till,
    unitTotalPayableInclVat,
    isUserIdentified
  });

  if (buttons.type === "none") {
    return (
      <SecondaryButton
        disabled
        icon={faExclamationTriangle}
        className="bg-kiosk-background-reversed-error text-kiosk-text-error"
      >
        <T _str="No payment methods available" />
      </SecondaryButton>
    );
  }

  const isDisabled = disabled || isPending;

  if (buttons.type === "choice") {
    return (
      <div className="flex w-full gap-5">
        {buttons.methods.includes("card") ? (
          <CardPayButton
            disabled={isDisabled}
            onClick={() => {
              submitPayment("card");
            }}
          />
        ) : null}
        {buttons.methods.includes("swish") ? (
          <SwishPayButton
            disabled={isDisabled}
            onClick={() => {
              submitPayment("swish");
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <PayButton
      disabled={isDisabled}
      totalAmount={
        hidePrices
          ? undefined
          : priceFormatter({
              value: unitTotalPayableInclVat,
              currency,
              locale
            })
      }
      onClick={() => {
        submitPayment(buttons.method);
      }}
    >
      {buttons.method === "credit" || buttons.method === "billing" ? (
        <T _str="Confirm order" />
      ) : (
        <T _str="Order & pay" />
      )}
    </PayButton>
  );
};
